"use client";

import { format } from "date-fns";
import { enUS, te } from "date-fns/locale";
import React, { useEffect, useState } from "react";
import { useParams, usePathname } from "next/navigation";
import { useQuery } from "@tanstack/react-query";

import CustomImage from "@/components/shared/CustomImage";
import { getLabel } from "@/data/labels";
import { getLocaleFromPathname } from "@/utils/getLocaleFromPath";
import Spacer from "@/components/shared/Spacer";
import { Typography } from "@/components/shared/Typography";
import { PhotogrpaherPhotosType } from "@/types/photoStory";
import { getImageUrl } from "@/utils/getImageUrl";
import { PHOTOGRAPHER_PAGE_RESULT_LIMIT } from "@/data/constants";
import { fetchSinglePhotographerPhotos } from "@/services/photoStoriesService";
import CustomPhotoSlider from "@/components/shared/CustomPhotoSlider";
import { formatDate } from "@/utils/dateUtils";

interface SinglePhotographerViewProps {
  initialData: PhotogrpaherPhotosType;
}

export default function SinglePhotographerView({ initialData }: SinglePhotographerViewProps) {
  const { photographer } = initialData;
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname) || "te-IN";
  const lang = (locale.split("-")[0] || "en") as "en" | "te";
  const [results, setResults] = useState(initialData?.results || []);
  const [filters, setFilters] = useState({
    limit: PHOTOGRAPHER_PAGE_RESULT_LIMIT,
    page: 1,
  });
  const [photoSliderOpen, setPhotoSliderOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const params = useParams();

  const { data, error, isLoading, isFetching } = useQuery({
    queryKey: ["fetchNewsByTag", filters.page, params?.slug],
    enabled: filters.page > 1,
    queryFn: () =>
      fetchSinglePhotographerPhotos({
        slug: params?.slug?.toString(),
        limit: filters?.limit,
        page: filters?.page,
      }),
  });

  const handleLoadMore = () => {
    setFilters(prev => ({ ...prev, page: prev.page + 1 }));
  };

  useEffect(() => {
    if (data) {
      setResults(prev => [...prev, ...(data?.results || [])]);
    }
  }, [data]);

  // Determine if we should show the Load More button
  const showLoadMore =
    results?.length > 0 &&
    ((filters.page === 1 && initialData?.totalDocs > results.length) ||
      (data && data?.totalDocs > results.length));

  return (
    <div className="container mt-8">
      <div className="container mt-6 text-center">
        <Typography variant="h1" className="text-[#E02026] font-extrabold uppercase">
          {getLabel(lang, "PHOTOGRAPHER")}
        </Typography>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-14 mt-6 sm:mt-14">
        <div className="bg-[#FF7A00] rounded-md text-center p-6">
          <div className="w-[100px] h-[100px] rounded-full overflow-hidden mx-auto">
            <img
              src={getImageUrl(photographer?.profilePicture?.filename) || "/images/avatar.webp"}
              alt={photographer?.firstName + " " + photographer?.lastName}
              className="object-cover w-full h-full"
            />
          </div>
          <Typography variant="h5" className="capitalize mt-2">
            {photographer?.firstName + " " + photographer?.lastName}
          </Typography>
          <Typography className="capitalize text-sm">{photographer?.role}</Typography>
          <span className="inline-block px-4 py-1 mt-2 text-xs font-medium text-white bg-black rounded-full">
            {initialData?.totalDocs} Photos
          </span>
        </div>

        {results?.map((result, index) => {
          return (
            <div key={index} className="group">
              <div className="relative w-full h-[200px]">
                <div
                  className="w-full h-full overflow-hidden rounded-md cursor-pointer"
                  onClick={() => {
                    setSelectedIndex(index);
                    setPhotoSliderOpen(true);
                  }}
                >
                  <CustomImage
                    src={getImageUrl(result?.media?.sizes?.sd?.filename || result?.media?.filename)}
                    className="object-cover w-full h-full img-zoom-hover"
                    alt={result?.media?.alt || ""}
                    phImg="/images/placeholder-grey-sd.png"
                  />
                </div>

                <div className="absolute top-[10px] left-[35px] transform -translate-x-1/2 bg-[#FF7A00] px-5  rotate-[270deg]">
                  <span className="text-sm font-bold uppercase whitespace-nowrap">
                    {formatDate(result?.date, lang, {
                      month: "long",
                      day: "2-digit",
                    })}
                  </span>
                </div>
              </div>
              <span
                className="block mt-3 cursor-pointer"
                onClick={() => {
                  setSelectedIndex(index);
                  setPhotoSliderOpen(true);
                }}
              >
                <Typography variant="h6" className="line-clamp-3">
                  {result?.caption}
                </Typography>
              </span>
            </div>
          );
        })}
      </div>

      {photoSliderOpen && (
        <CustomPhotoSlider
          title={photographer?.firstName + " " + photographer?.lastName}
          sliderData={results || []}
          setPhotoSliderOpen={setPhotoSliderOpen}
          selectedIndex={selectedIndex}
        />
      )}

      {showLoadMore && (
        <div className="flex items-center justify-center mt-8">
          <button
            className="load-more-btn"
            onClick={handleLoadMore}
            disabled={isFetching}
            aria-label={getLabel(lang, "LOAD_MORE")}
          >
            {getLabel(lang, "LOAD_MORE")}
          </button>
        </div>
      )}

      <Spacer size="lg" />
    </div>
  );
}
